import { useState } from 'react';
import { useGraphService } from '@repo/services';
import { useAsyncEffect } from './useAsyncEffect';

/**
 * Result type for a user photo lookup by name, containing the photo URL,
 * resolved user ID, and a loading flag.
 */
export interface UserPhotoByNameResult {
  photoUrl: string | undefined;
  userId: string | undefined;
  isLoading: boolean;
}

/**
 * Generates a unique sessionStorage cache key for a Microsoft Graph photo lookup,
 * prefixed with `graph-photo-by-name:` and lowercased.
 *
 * @param displayName - A user's display name string (e.g. "Ted Lucas" or "Lucas, Ted") used to look up their Microsoft Graph user record and photo.
 * @returns A cache key string like `"graph-photo-by-name:ted lucas"`.
 */
const PHOTO_CACHE_KEY = (name: string) => `graph-photo-by-name:${name}`;

/**
 * Resolves a Graph user photo from a display name string using a progressive
 * fallback chain:
 *
 *  1. displayName eq 'Ted Lucas'        (as-is / "First Last")
 *  2. displayName eq 'Lucas, Ted'       (flipped to "Last, First")
 *  3. givenName eq 'Ted' AND surname eq 'Lucas'   (field-level split)
 *
 * Input format "Last, First" is auto-detected and the chain runs in reverse
 * (comma form first, then space form, then field split).
 *
 * Results are cached in sessionStorage for the tab lifetime.
 */
export function useUserPhotoByName(displayName: string): UserPhotoByNameResult {
  const { graphClient } = useGraphService();

  const trimmed = displayName.trim();
  const cacheKey = PHOTO_CACHE_KEY(trimmed.toLowerCase());

  const [photoUrl, setPhotoUrl] = useState<string | undefined>(() => {
    try {
      return sessionStorage.getItem(cacheKey) ?? undefined;
    } catch { /* ignore */ }
    return undefined;
  });
  const [userId, setUserId] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(() => {
    // Only skip loading if we have a cached photo URL
    try { return !sessionStorage.getItem(cacheKey); } catch { return true; }
  });

  useAsyncEffect(async () => {
    if (!trimmed || !graphClient) return;
    // Already resolved (photo or confirmed not-found)
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) return;
    } catch {
      /* ignore */
    }

    setIsLoading(true);

    // ── Build ordered list of candidate filters ──────────────────────────────
    // Detect "Last, First" vs "First Last"
    const isCommaForm = trimmed.includes(',');
    const commaForm = isCommaForm
      ? trimmed // already "Last, First"
      : (() => {
          const parts = trimmed.split(/\s+/);
          return parts.length >= 2 ? `${parts[parts.length - 1]}, ${parts.slice(0, -1).join(' ')}` : trimmed;
        })();
    const spaceForm = isCommaForm
      ? trimmed
          .split(',')
          .map((s) => s.trim())
          .reverse()
          .join(' ') // "Last, First" → "First Last"
      : trimmed;

    // "First Last" parts for field-level search
    const spaceParts = spaceForm.split(/\s+/).filter(Boolean);
    const givenName = spaceParts[0] ?? '';
    const surname = spaceParts[spaceParts.length - 1] ?? '';

    // guessedEmail is first char of givenName + full surname + @agreserves.com
    const guessedEmail = givenName.slice(0, 1).toLowerCase() + surname.toLowerCase() + '@agreserves.com';

    interface FilterAttempt { filter: string }

    // Each attempt carries its own ready-to-use OData filter string.
    // Order: UPN guess → comma form → space form → field-level split
    const attempts: FilterAttempt[] = [
      { filter: `userPrincipalName eq '${guessedEmail}'` },
      { filter: `displayName eq '${commaForm}'` },
      { filter: `displayName eq '${spaceForm}'` },
      ...(givenName && surname && givenName !== surname
        ? [{ filter: `givenName eq '${givenName}' and surname eq '${surname}'` }]
        : []),
    ];

    try {
      let resolvedId: string | undefined;

      for (const { filter } of attempts) {
        try {
          const result = await graphClient
            .api('/users')
            .header('ConsistencyLevel', 'eventual')
            .query({ $count: 'true' })
            .filter(filter)
            .select('id,mail,displayName')
            .get();
          const user = result?.value?.[0];
          if (user?.id) {
            resolvedId = user.id;
            break; // found — stop trying
          }
        } catch {
          // This attempt failed (network / permission) — try next
        }
      }

      if (!resolvedId) {
        // All attempts exhausted — nothing to cache, will retry on next mount
        return;
      }

      setUserId(resolvedId);

      // ── Fetch photo ──────────────────────────────────────────────────────
      try {
        const blob: Blob = await graphClient.api(`/users/${resolvedId}/photo/$value`).get();
        const url = window.URL.createObjectURL(blob);
        try {
          sessionStorage.setItem(cacheKey, url);
        } catch {
          /* quota */
        }
        setPhotoUrl(url);
      } catch {
        // User found but has no photo — leave uncached so we can retry
      }
    } finally {
      setIsLoading(false);
    }
  }, [trimmed, graphClient]);

  return { photoUrl, userId, isLoading };
}
